export const EDUCATION_RICH_TEXT_FORMAT = "evolys-rich-text-v1";

export type EducationRichTextMarkType =
  | "bold"
  | "italic"
  | "underline"
  | "strike"
  | "code"
  | "link";

export interface EducationRichTextMark {
  type: EducationRichTextMarkType | string;
  attrs?: Record<string, unknown>;
}

export interface EducationRichTextNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: EducationRichTextNode[];
  marks?: EducationRichTextMark[];
  text?: string;
}

export interface EducationRichTextEnvelope {
  format: typeof EDUCATION_RICH_TEXT_FORMAT;
  document: EducationRichTextNode;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isRichTextNode(value: unknown): value is EducationRichTextNode {
  return isRecord(value) && typeof value.type === "string";
}

export function parseEducationRichText(
  value: string,
): EducationRichTextEnvelope | null {
  if (!value.trimStart().startsWith("{")) return null;

  try {
    const parsed: unknown = JSON.parse(value);
    if (
      !isRecord(parsed) ||
      parsed.format !== EDUCATION_RICH_TEXT_FORMAT ||
      !isRichTextNode(parsed.document) ||
      parsed.document.type !== "doc"
    ) {
      return null;
    }
    return parsed as unknown as EducationRichTextEnvelope;
  } catch {
    return null;
  }
}

export function serializeEducationRichText(
  document: EducationRichTextNode,
): string {
  return JSON.stringify({
    format: EDUCATION_RICH_TEXT_FORMAT,
    document,
  } satisfies EducationRichTextEnvelope);
}

function textNode(value: string): EducationRichTextNode {
  return { type: "text", text: value };
}

function paragraph(value: string): EducationRichTextNode {
  return {
    type: "paragraph",
    content: value ? [textNode(value)] : undefined,
  };
}

/**
 * Converts the original Markdown-like seed content into the editor's document
 * model. Existing articles remain readable and are upgraded only when saved.
 */
export function educationBodyToEditorDocument(
  value: string,
): EducationRichTextNode {
  const rich = parseEducationRichText(value);
  if (rich) return rich.document;

  const content: EducationRichTextNode[] = [];
  for (const rawBlock of value.split(/\n{2,}/)) {
    const block = rawBlock.trim();
    if (!block) continue;

    const heading = /^(#{2,4})\s+(.+)$/.exec(block);
    if (heading) {
      content.push({
        type: "heading",
        attrs: { level: heading[1].length },
        content: [textNode(heading[2])],
      });
      continue;
    }

    const lines = block.split("\n").map((line) => line.trim());
    if (lines.every((line) => line.startsWith("- "))) {
      content.push({
        type: "bulletList",
        content: lines.map((line) => ({
          type: "listItem",
          content: [paragraph(line.slice(2))],
        })),
      });
      continue;
    }

    if (lines.every((line) => /^\d+\.\s+/.test(line))) {
      content.push({
        type: "orderedList",
        attrs: { start: 1 },
        content: lines.map((line) => ({
          type: "listItem",
          content: [paragraph(line.replace(/^\d+\.\s+/, ""))],
        })),
      });
      continue;
    }

    if (lines.every((line) => line.startsWith("> "))) {
      content.push({
        type: "blockquote",
        content: [paragraph(lines.map((line) => line.slice(2)).join(" "))],
      });
      continue;
    }

    content.push(paragraph(lines.join(" ")));
  }

  return {
    type: "doc",
    content: content.length ? content : [paragraph("")],
  };
}

function collectText(node: EducationRichTextNode): string {
  const own = node.type === "text" ? node.text ?? "" : "";
  const children = (node.content ?? []).map(collectText).join(" ");
  return `${own} ${children}`.trim();
}

export function educationBodyPlainText(value: string): string {
  const rich = parseEducationRichText(value);
  if (rich) return collectText(rich.document).replace(/\s+/g, " ").trim();

  return value
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*(?:[-*>]|\d+\.)\s+/gm, "")
    .replace(/[*_~`[\]()]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
